/**
 * JSON / CSV export of the loaded metric payloads (same files the old page produced:
 * session-<id>-<metrics>.json|csv). JSON keeps the payloads as-is, keyed by metric; CSV
 * flattens every record to one row, with a leading `metric` column when several are exported.
 */

function save(text, type, filename) {
    const blob = new Blob([text], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** { a: { b: 1 }, c: [1, 2] } → { 'a.b': 1, c: '1;2' } */
export function flattenObject(obj, prefix = '', out = {}) {
    Object.entries(obj || {}).forEach(([key, val]) => {
        const k = prefix ? `${prefix}.${key}` : key;
        if (Array.isArray(val)) out[k] = val.map((x) => (x && typeof x === 'object' ? JSON.stringify(x) : x)).join(';');
        else if (val && typeof val === 'object') flattenObject(val, k, out);
        else out[k] = val;
    });
    return out;
}

/** One flat row per record, per metric shape. */
function rowsFor(metric, frames) {
    if (metric === 'skeleton') {
        return frames.map((f) => {
            const row = { timestamp: f?.timestamp };
            (Array.isArray(f?.data) ? f.data : []).forEach((j) => {
                if (!j?.bodyPart) return;
                flattenObject({ position: j.position, orientation: j.orientation }, j.bodyPart, row);
            });
            return row;
        });
    }
    if (metric === 'parameter') {
        const rows = [];
        frames.forEach((g) => {
            (Array.isArray(g?.data) ? g.data : []).forEach((p) => {
                rows.push({ timestamp: g.timestamp, group: g.name, name: p?.name, value: p?.value_ != null && typeof p.value_ === 'object' ? JSON.stringify(p.value_) : p?.value_, type: p?.parm_type, min: p?.min_, max: p?.max_, strings: Array.isArray(p?.strings) ? p.strings.join(';') : '' });
            });
        });
        return rows;
    }
    return frames.map((f) => flattenObject(f));
}

/**
 * One CSV cell. Text a game posted that starts with = + - @ (or a tab / CR) would run as a
 * formula when the file is opened in a spreadsheet; a leading ' keeps it text. Plain
 * numbers, negative ones too, stay numbers (security re-audit 2026-10-16).
 */
export const cell = (v) => {
    if (v == null) return '';
    let s = String(v);
    if (/^[=+\-@\t\r]/.test(s) && !/^[-+]?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(s)) s = `'${s}`;
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function toCSV(byMetric) {
    const keys = Object.keys(byMetric);
    const multi = keys.length > 1;
    const rows = [];
    keys.forEach((m) => rowsFor(m, byMetric[m] || []).forEach((r) => rows.push(multi ? { metric: m, ...r } : r)));
    const headers = [];
    const seen = new Set();
    rows.forEach((r) => Object.keys(r).forEach((h) => { if (!seen.has(h)) { seen.add(h); headers.push(h); } }));
    return [headers.map(cell).join(','), ...rows.map((r) => headers.map((h) => cell(r[h])).join(','))].join('\n');
}

const base = (id, keys) => `session-${id}-${keys.join('-')}`;

export function downloadJSON(id, byMetric) {
    const keys = Object.keys(byMetric);
    if (!keys.length) return;
    save(JSON.stringify(byMetric, null, 2), 'application/json', `${base(id, keys)}.json`);
}

export function downloadCSV(id, byMetric) {
    const keys = Object.keys(byMetric);
    if (!keys.length) return;
    save(toCSV(byMetric), 'text/csv', `${base(id, keys)}.csv`);
}
